import React, { useState, useEffect, useRef } from 'react';
import { CHAIN_PHASE } from '../shared/fiveLeague';
import VoiceInput, { isVoiceInputSupported } from '../components/VoiceInput';
import {
  CHAIN_QUESTION_TYPE,
  CHAIN_GAME_PHASE,
  PANEL_STATUS,
  PANEL_LAYOUTS,
  PANEL_QUESTIONS,
  validatePanelAnswer,
  getLayoutInfo,
} from '../constants/chainQuestions';
import './ChainTVTest.css';

// ゲームモード
const GAME_MODE = {
  CHAIN: 'chain',
  PANEL: 'panel',
};

// モックプレイヤーリスト
const ALL_PLAYERS = [
  { playerId: 'p1', nickname: 'アリス', emoji: '😀' },
  { playerId: 'p2', nickname: 'ボブ', emoji: '😎' },
  { playerId: 'p3', nickname: 'キャロル', emoji: '🥳' },
  { playerId: 'p4', nickname: 'デイブ', emoji: '😤' },
  { playerId: 'p5', nickname: 'エミリー', emoji: '🤩' },
  { playerId: 'p6', nickname: 'フランク', emoji: '🧐' },
];

// チェーン問題リスト（既存）
const CHAIN_QUESTIONS_LOCAL = [
  { prompt: '赤い果物を答えてください', answers: ['りんご', 'いちご', 'さくらんぼ', 'すいか', 'トマト', 'ざくろ', 'ラズベリー', 'クランベリー', 'あんず', 'ドラゴンフルーツ'] },
  { prompt: '黄色い食べ物を答えてください', answers: ['バナナ', 'レモン', 'とうもろこし', 'たまご', 'カレー', 'チーズ', 'パイナップル', 'かぼちゃ', 'マンゴー', 'グレープフルーツ'] },
  { prompt: '丸いものを答えてください', answers: ['ボール', '地球', '月', 'りんご', '時計', 'コイン', 'ピザ', 'ドーナツ', 'スイカ', 'メロン'] },
  { prompt: '海にいる生き物を答えてください', answers: ['魚', 'クジラ', 'イルカ', 'タコ', 'イカ', 'エビ', 'カニ', 'クラゲ', 'サメ', 'ウミガメ'] },
  { prompt: '空を飛ぶものを答えてください', answers: ['飛行機', '鳥', 'ヘリコプター', '風船', 'ドローン', '蝶', 'トンボ', 'ロケット', '凧', 'UFO'] },
  { prompt: '学校にあるものを答えてください', answers: ['机', '椅子', '黒板', 'チョーク', '教科書', 'ノート', '鉛筆', '消しゴム', '定規', 'ランドセル'] },
  { prompt: '冬に関係するものを答えてください', answers: ['雪', 'こたつ', 'みかん', 'スキー', 'クリスマス', 'マフラー', '手袋', '鍋', '温泉', '年越しそば'] },
  { prompt: '日本の都道府県を答えてください', answers: ['東京', '大阪', '北海道', '京都', '福岡', '沖縄', '神奈川', '愛知', '広島', '宮城'] },
];

function ChainTVTest() {
  // ===== 共通設定 =====
  const [gameMode, setGameMode] = useState(GAME_MODE.CHAIN);
  const [playerCount, setPlayerCount] = useState(4);
  const [showControls, setShowControls] = useState(true);

  // ===== チェーンモード設定 =====
  const [answerCount, setAnswerCount] = useState(5);
  const [questionIndex, setQuestionIndex] = useState(0);

  // ===== チェーンモード ゲーム状態 =====
  const [phase, setPhase] = useState(CHAIN_PHASE.ANSWERING);
  const [currentSlot, setCurrentSlot] = useState(0);
  const directionRef = useRef(1); // 1: 右へ, -1: 左へ（折り返し用、同期的に更新するためref）
  const [remainingTime, setRemainingTime] = useState(30);
  const [meteorPosition, setMeteorPosition] = useState(0);
  const [submittedAnswers, setSubmittedAnswers] = useState([]);
  const [correctCount, setCorrectCount] = useState(0);
  const [showCorrect, setShowCorrect] = useState(false);
  const [showWrong, setShowWrong] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const answerIndexRef = useRef(0);

  // ===== パネルモード 設定・状態 =====
  const [panelQuestionIndex, setPanelQuestionIndex] = useState(0);
  const [panelPhase, setPanelPhase] = useState(CHAIN_GAME_PHASE.WAITING);
  const [panelStatuses, setPanelStatuses] = useState({}); // { panelId: 'available' | 'answered' }
  const [currentPreviewIndex, setCurrentPreviewIndex] = useState(0);
  const [selectedPanelId, setSelectedPanelId] = useState(null);
  const [panelCorrectCount, setPanelCorrectCount] = useState(0);
  const [panelRemainingTime, setPanelRemainingTime] = useState(15);

  // 音声入力関連
  const [micEnabled, setMicEnabled] = useState(false);
  const [voiceInterim, setVoiceInterim] = useState('');
  const voiceSupported = isVoiceInputSupported();

  const timerRef = useRef(null);
  const previewTimerRef = useRef(null);

  // 現在の問題と回答（チェーンモード）
  const currentChainQuestion = CHAIN_QUESTIONS_LOCAL[questionIndex % CHAIN_QUESTIONS_LOCAL.length];
  // 現在のパネル問題
  const currentPanelQuestion = PANEL_QUESTIONS[panelQuestionIndex % PANEL_QUESTIONS.length];
  const players = ALL_PLAYERS.slice(0, playerCount);
  const getTotalTime = (count) => count === 10 ? 60 : 30;
  const totalTime = getTotalTime(answerCount);

  // チェーンモード 設定変更時にリセット
  useEffect(() => {
    if (gameMode !== GAME_MODE.CHAIN) return;
    setPhase(CHAIN_PHASE.ANSWERING);
    setCurrentSlot(0);
    directionRef.current = 1;
    setRemainingTime(getTotalTime(answerCount));
    setMeteorPosition(0);
    setSubmittedAnswers([]);
    setCorrectCount(0);
    setIsRunning(false);
    setShowCorrect(false);
    setShowWrong(false);
    answerIndexRef.current = 0;
  }, [answerCount, playerCount, gameMode]);

  // パネルモード 問題変更時にリセット
  useEffect(() => {
    if (gameMode !== GAME_MODE.PANEL) return;
    resetPanelGame();
  }, [panelQuestionIndex, gameMode]);

  // パネルモード リセット関数
  const resetPanelGame = () => {
    setPanelPhase(CHAIN_GAME_PHASE.WAITING);
    setCurrentPreviewIndex(0);
    setSelectedPanelId(null);
    setPanelCorrectCount(0);
    setPanelRemainingTime(currentPanelQuestion?.answerTimeLimit || 15);
    setShowCorrect(false);
    setShowWrong(false);
    // パネル状態を初期化
    const initialStatuses = {};
    currentPanelQuestion?.panels?.forEach(p => {
      initialStatuses[p.id] = PANEL_STATUS.AVAILABLE;
    });
    setPanelStatuses(initialStatuses);
    if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
    if (timerRef.current) clearInterval(timerRef.current);
  };

  // パネルプレビュー自動進行
  useEffect(() => {
    if (gameMode !== GAME_MODE.PANEL || panelPhase !== CHAIN_GAME_PHASE.PREVIEW) return;

    const totalPanels = currentPanelQuestion?.panels?.length || 0;
    const previewDuration = (currentPanelQuestion?.previewDuration || 3) * 1000;

    previewTimerRef.current = setTimeout(() => {
      if (currentPreviewIndex < totalPanels - 1) {
        setCurrentPreviewIndex(prev => prev + 1);
      } else {
        // プレビュー完了 → 選択フェーズへ
        setPanelPhase(CHAIN_GAME_PHASE.SELECT);
        setCurrentPreviewIndex(0);
      }
    }, previewDuration);

    return () => clearTimeout(previewTimerRef.current);
  }, [gameMode, panelPhase, currentPreviewIndex, currentPanelQuestion]);

  // パネル回答フェーズのタイマー（隕石も動かす）
  useEffect(() => {
    if (gameMode !== GAME_MODE.PANEL || panelPhase !== CHAIN_GAME_PHASE.ANSWER) return;

    const panelTotalTime = currentPanelQuestion?.answerTimeLimit || 15;
    setMeteorPosition(0); // 回答開始時にリセット

    timerRef.current = setInterval(() => {
      setPanelRemainingTime(prev => {
        if (prev <= 0) {
          clearInterval(timerRef.current);
          // 時間切れ → 不正解扱いで選択フェーズに戻る
          setShowWrong(true);
          setTimeout(() => {
            setShowWrong(false);
            setSelectedPanelId(null);
            setPanelPhase(CHAIN_GAME_PHASE.SELECT);
            setPanelRemainingTime(panelTotalTime);
            setMeteorPosition(0);
          }, 1000);
          return 0;
        }
        const newTime = prev - 0.1;
        // 隕石の位置を更新
        setMeteorPosition(((panelTotalTime - newTime) / panelTotalTime) * 100);
        return newTime;
      });
    }, 100);

    return () => clearInterval(timerRef.current);
  }, [gameMode, panelPhase, currentPanelQuestion]);

  // タイマー
  useEffect(() => {
    if (!isRunning) return;

    timerRef.current = setInterval(() => {
      setRemainingTime(prev => {
        if (prev <= 0) {
          setIsRunning(false);
          setPhase(CHAIN_PHASE.TIMEOUT);
          return 0;
        }
        const newTime = prev - 0.1;
        setMeteorPosition(((totalTime - newTime) / totalTime) * 100);
        return newTime;
      });
    }, 100);

    return () => clearInterval(timerRef.current);
  }, [isRunning, totalTime]);

  // ===== チェーンモード 正解処理（手動ボタン用） =====
  const handleCorrect = () => {
    const answerIdx = answerIndexRef.current;
    const newAnswer = currentChainQuestion.answers[answerIdx % currentChainQuestion.answers.length];
    answerIndexRef.current = answerIdx + 1;

    setSubmittedAnswers(prev => [...prev, { text: newAnswer, playerIdx: currentSlot }]);
    setCorrectCount(prev => prev + 1);
    setShowCorrect(true);

    setTimeout(() => {
      setShowCorrect(false);
      if (answerIdx + 1 >= answerCount) {
        setPhase(CHAIN_PHASE.COMPLETE);
        setIsRunning(false);
      } else {
        moveToNextSlot();
      }
    }, 1000);
  };

  // 次のスロットに移動（折り返しロジック）
  const moveToNextSlot = () => {
    setCurrentSlot(prev => {
      const next = prev + directionRef.current;
      if (next >= playerCount - 1) {
        directionRef.current = -1; // 右端に来たら左へ
        return playerCount - 1;
      } else if (next <= 0) {
        directionRef.current = 1; // 左端に来たら右へ
        return 0;
      }
      return next;
    });
  };

  // 不正解処理
  const handleWrong = () => {
    setShowWrong(true);
    setTimeout(() => setShowWrong(false), 1000);
  };

  // ===== 音声認識結果 - モードに応じて処理 =====
  const handleVoiceResult = (text) => {
    console.log('[ChainTVTest] Voice result:', text);
    setVoiceInterim('');

    if (gameMode === GAME_MODE.PANEL) {
      handlePanelVoiceResult(text);
    } else {
      handleChainVoiceResult(text);
    }
  };

  // チェーンモード用音声結果処理
  const handleChainVoiceResult = (text) => {
    // 既に回答済みの答えを除外
    const usedAnswers = submittedAnswers.map(a => a.text.toLowerCase());
    const normalizedText = text.toLowerCase().trim();

    // 有効な回答リストから検索（部分一致も許可）
    const validAnswers = currentChainQuestion.answers.filter(a => !usedAnswers.includes(a.toLowerCase()));
    const isCorrect = validAnswers.some(answer =>
      normalizedText.includes(answer.toLowerCase()) ||
      answer.toLowerCase().includes(normalizedText)
    );

    console.log('[ChainTVTest] Chain answer check:', { text: normalizedText, isCorrect, validAnswers: validAnswers.slice(0, 5) });

    if (isCorrect) {
      const matchedAnswer = validAnswers.find(answer =>
        normalizedText.includes(answer.toLowerCase()) ||
        answer.toLowerCase().includes(normalizedText)
      );
      handleCorrectWithAnswer(matchedAnswer || text);
    } else {
      handleWrong();
    }
  };

  // パネルモード用音声結果処理
  const handlePanelVoiceResult = (text) => {
    if (panelPhase !== CHAIN_GAME_PHASE.ANSWER || !selectedPanelId) return;

    const panel = currentPanelQuestion.panels.find(p => p.id === selectedPanelId);
    if (!panel) return;

    const isCorrect = validatePanelAnswer(panel, text);
    console.log('[ChainTVTest] Panel answer check:', { text, panel: panel.answer, isCorrect });

    if (isCorrect) {
      handlePanelCorrect(panel);
    } else {
      handleWrong();
    }
  };

  // ===== パネルモード専用関数 =====

  // パネル正解処理
  const handlePanelCorrect = (panel) => {
    clearInterval(timerRef.current);
    setShowCorrect(true);

    // パネルを正解済みにする
    setPanelStatuses(prev => ({
      ...prev,
      [panel.id]: PANEL_STATUS.ANSWERED,
    }));
    setPanelCorrectCount(prev => prev + 1);

    const totalPanels = currentPanelQuestion.panels.length;
    const newCorrectCount = panelCorrectCount + 1;

    setTimeout(() => {
      setShowCorrect(false);
      setSelectedPanelId(null);

      if (newCorrectCount >= totalPanels) {
        // 全パネル正解
        setPanelPhase(CHAIN_GAME_PHASE.COMPLETE);
      } else {
        // 選択フェーズに戻る
        setPanelPhase(CHAIN_GAME_PHASE.SELECT);
        setPanelRemainingTime(currentPanelQuestion?.answerTimeLimit || 15);
      }
    }, 1500);
  };

  // パネル選択処理
  const handlePanelSelect = (panelId) => {
    if (panelPhase !== CHAIN_GAME_PHASE.SELECT) return;
    if (panelStatuses[panelId] === PANEL_STATUS.ANSWERED) return;

    setSelectedPanelId(panelId);
    setPanelPhase(CHAIN_GAME_PHASE.ANSWER);
    setPanelRemainingTime(currentPanelQuestion?.answerTimeLimit || 15);
    setMicEnabled(true); // 自動でマイクON
  };

  // パネルゲーム開始
  const startPanelGame = () => {
    resetPanelGame();
    setMicEnabled(true); // マイク自動ON
    setPanelPhase(CHAIN_GAME_PHASE.PREVIEW);
  };

  // ===== チェーンモード 正解処理（回答テキスト指定版） =====
  const handleCorrectWithAnswer = (answerText) => {
    setSubmittedAnswers(prev => [...prev, { text: answerText, playerIdx: currentSlot }]);
    setCorrectCount(prev => prev + 1);
    setShowCorrect(true);

    const newCount = correctCount + 1;
    setTimeout(() => {
      setShowCorrect(false);
      if (newCount >= answerCount) {
        setPhase(CHAIN_PHASE.COMPLETE);
        setIsRunning(false);
      } else {
        moveToNextSlot();
      }
    }, 1000);
  };

  // リセット（チェーンモード）
  const handleReset = () => {
    if (gameMode === GAME_MODE.PANEL) {
      resetPanelGame();
      return;
    }
    setPhase(CHAIN_PHASE.ANSWERING);
    setCurrentSlot(0);
    directionRef.current = 1;
    setRemainingTime(totalTime);
    setMeteorPosition(0);
    setSubmittedAnswers([]);
    setCorrectCount(0);
    setIsRunning(false);
    setShowCorrect(false);
    setShowWrong(false);
    answerIndexRef.current = 0;
  };

  // 問題変更
  const handleNextQuestion = () => {
    if (gameMode === GAME_MODE.PANEL) {
      setPanelQuestionIndex(prev => (prev + 1) % PANEL_QUESTIONS.length);
    } else {
      setQuestionIndex(prev => (prev + 1) % CHAIN_QUESTIONS_LOCAL.length);
      handleReset();
    }
  };

  // ===== 結果画面レンダリング =====

  // チェーンモード タイムアウト画面
  if (gameMode === GAME_MODE.CHAIN && phase === CHAIN_PHASE.TIMEOUT) {
    return (
      <div className="chain-tv-test timeout">
        <div className="result-overlay">
          <span className="result-icon">💥</span>
          <h1 className="result-title">TIME UP!</h1>
          <p className="result-score">{correctCount} / {answerCount}</p>
          <button onClick={handleReset} className="reset-btn">もう一度</button>
        </div>
      </div>
    );
  }

  // チェーンモード 全問正解画面
  if (gameMode === GAME_MODE.CHAIN && phase === CHAIN_PHASE.COMPLETE) {
    return (
      <div className="chain-tv-test complete">
        <div className="result-overlay">
          <span className="result-icon">🎉</span>
          <h1 className="result-title">PERFECT!</h1>
          <p className="result-score">{answerCount}問 全問正解！</p>
          <button onClick={handleReset} className="reset-btn">もう一度</button>
        </div>
      </div>
    );
  }

  // パネルモード 全問正解画面
  if (gameMode === GAME_MODE.PANEL && panelPhase === CHAIN_GAME_PHASE.COMPLETE) {
    return (
      <div className="chain-tv-test complete panel-mode">
        <div className="result-overlay">
          <span className="result-icon">🎉</span>
          <h1 className="result-title">ALL CLEAR!</h1>
          <p className="result-score">{currentPanelQuestion.panels.length}パネル 全問正解！</p>
          <button onClick={handleReset} className="reset-btn">もう一度</button>
        </div>
      </div>
    );
  }

  // ===== パネルモード レンダリング =====
  // パネルモードでも隕石+チューブUIは共通、左にパネル一覧を表示
  if (gameMode === GAME_MODE.PANEL) {
    const selectedPanel = selectedPanelId
      ? currentPanelQuestion.panels.find(p => p.id === selectedPanelId)
      : null;
    const previewPanel = currentPanelQuestion.panels[currentPreviewIndex];
    const isPanelRunning = panelPhase === CHAIN_GAME_PHASE.PREVIEW ||
                           panelPhase === CHAIN_GAME_PHASE.SELECT ||
                           panelPhase === CHAIN_GAME_PHASE.ANSWER;

    return (
      <div className="chain-tv-test panel-mode">
        {/* 正解オーバーレイ */}
        {showCorrect && (
          <div className="overlay correct">
            <span className="overlay-icon">⭕</span>
            <span className="overlay-text">正解！</span>
          </div>
        )}

        {/* 不正解オーバーレイ */}
        {showWrong && (
          <div className="overlay wrong">
            <span className="overlay-icon">❌</span>
            <span className="overlay-text">ブブー</span>
          </div>
        )}

        {/* コントロールトグル */}
        <button
          className="control-toggle"
          onClick={() => setShowControls(!showControls)}
        >
          {showControls ? '🎬 TVモード' : '🧪 設定'}
        </button>

        {/* コントロールパネル */}
        {showControls && (
          <div className="control-panel">
            <h3>🧪 テスト設定</h3>

            <div className="control-group">
              <label>モード:</label>
              <div className="button-group">
                <button
                  className={gameMode === GAME_MODE.CHAIN ? 'active' : ''}
                  onClick={() => setGameMode(GAME_MODE.CHAIN)}
                >
                  ⚡ チェーン
                </button>
                <button
                  className={gameMode === GAME_MODE.PANEL ? 'active' : ''}
                  onClick={() => setGameMode(GAME_MODE.PANEL)}
                >
                  🔲 パネル
                </button>
              </div>
            </div>

            <div className="control-group">
              <label>人数:</label>
              <div className="button-group">
                {[2, 3, 4, 5, 6].map(n => (
                  <button
                    key={n}
                    className={playerCount === n ? 'active' : ''}
                    onClick={() => setPlayerCount(n)}
                  >
                    {n}人
                  </button>
                ))}
              </div>
            </div>

            <div className="control-group">
              <label>問題:</label>
              <button onClick={handleNextQuestion} className="question-btn">
                🔄 次の問題
              </button>
            </div>

            <hr />

            <div className="control-group">
              <button onClick={startPanelGame} className="action-btn start">
                ▶️ 開始（マイク自動ON）
              </button>
            </div>

            <div className="control-group">
              <button onClick={handleReset} className="action-btn reset">🔄 リセット</button>
            </div>

            {voiceSupported && (
              <div className="control-group">
                <button
                  onClick={() => setMicEnabled(!micEnabled)}
                  className={`action-btn mic ${micEnabled ? 'on' : ''}`}
                >
                  {micEnabled ? '🎤 マイクON' : '🔇 マイクOFF'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* ヘッダー */}
        <div className="tv-header">
          <div className="timer">
            <span className={`timer-value ${panelRemainingTime <= 10 ? 'warning' : ''} ${panelRemainingTime <= 5 ? 'critical' : ''}`}>
              {Math.ceil(panelRemainingTime)}
            </span>
            <span className="timer-label">秒</span>
          </div>
          <div className="title">🔲 PANEL QUIZ</div>
          <div className="progress">
            <span className="progress-current">{panelCorrectCount}</span>
            <span className="progress-sep">/</span>
            <span className="progress-total">{currentPanelQuestion.panels.length}</span>
          </div>
        </div>

        {/* 問題文 */}
        <div className="question-area">
          <p className="question-text">{currentPanelQuestion.prompt}</p>
        </div>

        {/* メインコンテンツ：左パネル一覧 + 中央チューブ */}
        <div className="panel-main-area">
          {/* 左：パネル一覧（縦1列） */}
          <div className="panel-sidebar">
            {currentPanelQuestion.panels.map(panel => {
              const isAnswered = panelStatuses[panel.id] === PANEL_STATUS.ANSWERED;
              const isSelected = selectedPanelId === panel.id;
              const isPreviewing = panelPhase === CHAIN_GAME_PHASE.PREVIEW && currentPreviewIndex === panel.id - 1;
              return (
                <div
                  key={panel.id}
                  className={`panel-sidebar-item ${isAnswered ? 'answered' : 'available'} ${isSelected ? 'selected' : ''} ${isPreviewing ? 'previewing' : ''}`}
                  onClick={() => panelPhase === CHAIN_GAME_PHASE.SELECT && !isAnswered && handlePanelSelect(panel.id)}
                >
                  <div className="panel-sidebar-number">{panel.id}</div>
                  <div className="panel-sidebar-content">
                    {isAnswered ? (
                      <span className="panel-sidebar-answer">{panel.answer}</span>
                    ) : (
                      <span className="panel-sidebar-text">{panel.text}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* 中央：チューブエリア（プレイヤー+隕石） */}
          <div className="tubes-area">
            {players.map((player, idx) => {
              const isCurrent = idx === currentSlot;
              return (
                <div key={player.playerId} className={`player-tube ${isCurrent ? 'current' : ''}`}>
                  <div className="tube">
                    {isCurrent && isPanelRunning && (
                      <div
                        className={`meteor ${meteorPosition > 70 ? 'danger' : ''} ${meteorPosition > 90 ? 'critical' : ''}`}
                        style={{ top: `${Math.min(meteorPosition, 85)}%` }}
                      >
                        <span className="meteor-icon">☄️</span>
                      </div>
                    )}
                    <div className="tube-glow"></div>
                  </div>
                  <div className={`player-icon ${isCurrent ? 'active' : ''}`}>
                    <span className="player-emoji">{player.emoji}</span>
                  </div>
                  <div className="player-name">{player.nickname}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* プレビュー時のポップアップ */}
        {panelPhase === CHAIN_GAME_PHASE.PREVIEW && previewPanel && (
          <div className="panel-popup-overlay">
            <div className="panel-popup">
              <div className="preview-indicator">
                プレビュー {currentPreviewIndex + 1} / {currentPanelQuestion.panels.length}
              </div>
              <div className="panel-popup-number">{previewPanel.id}</div>
              <div className="panel-popup-text">{previewPanel.text}</div>
            </div>
          </div>
        )}

        {/* 回答時の選択パネル表示 */}
        {panelPhase === CHAIN_GAME_PHASE.ANSWER && selectedPanel && (
          <div className="panel-popup-overlay answering">
            <div className="panel-popup answering">
              <div className="panel-popup-number">{selectedPanel.id}</div>
              <div className="panel-popup-text">{selectedPanel.text}</div>
              <div className="answer-instruction">🎤 声で回答！</div>
            </div>
          </div>
        )}

        {/* 待機中メッセージ */}
        {panelPhase === CHAIN_GAME_PHASE.WAITING && (
          <div className="panel-waiting-message">
            「開始」を押してください
          </div>
        )}

        {/* 選択フェーズメッセージ */}
        {panelPhase === CHAIN_GAME_PHASE.SELECT && (
          <div className="panel-select-message">
            パネルを選んでください →
          </div>
        )}

        {/* 音声入力 */}
        {voiceSupported && (
          <div className="voice-section" style={{ display: micEnabled ? 'block' : 'none' }}>
            {voiceInterim && (
              <div className="voice-interim">認識中: {voiceInterim}</div>
            )}
            <VoiceInput
              onResult={handleVoiceResult}
              onInterim={(text) => setVoiceInterim(text)}
              disabled={panelPhase !== CHAIN_GAME_PHASE.ANSWER || !micEnabled}
              autoStart={panelPhase === CHAIN_GAME_PHASE.ANSWER && micEnabled}
              keepListening={true}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="chain-tv-test">
      {/* 正解オーバーレイ */}
      {showCorrect && (
        <div className="overlay correct">
          <span className="overlay-icon">⭕</span>
          <span className="overlay-text">正解！</span>
        </div>
      )}

      {/* 不正解オーバーレイ */}
      {showWrong && (
        <div className="overlay wrong">
          <span className="overlay-icon">❌</span>
          <span className="overlay-text">ブブー</span>
        </div>
      )}

      {/* コントロールトグル */}
      <button
        className="control-toggle"
        onClick={() => setShowControls(!showControls)}
      >
        {showControls ? '🎬 TVモード' : '🧪 設定'}
      </button>

      {/* コントロールパネル */}
      {showControls && (
        <div className="control-panel">
          <h3>🧪 テスト設定</h3>

          <div className="control-group">
            <label>モード:</label>
            <div className="button-group">
              <button
                className={gameMode === GAME_MODE.CHAIN ? 'active' : ''}
                onClick={() => setGameMode(GAME_MODE.CHAIN)}
              >
                ⚡ チェーン
              </button>
              <button
                className={gameMode === GAME_MODE.PANEL ? 'active' : ''}
                onClick={() => setGameMode(GAME_MODE.PANEL)}
              >
                🔲 パネル
              </button>
            </div>
          </div>

          <div className="control-group">
            <label>人数:</label>
            <div className="button-group">
              {[2, 3, 4, 5, 6].map(n => (
                <button
                  key={n}
                  className={playerCount === n ? 'active' : ''}
                  onClick={() => setPlayerCount(n)}
                >
                  {n}人
                </button>
              ))}
            </div>
          </div>

          <div className="control-group">
            <label>問題数:</label>
            <div className="button-group">
              <button
                className={answerCount === 5 ? 'active' : ''}
                onClick={() => setAnswerCount(5)}
              >
                5問
              </button>
              <button
                className={answerCount === 10 ? 'active' : ''}
                onClick={() => setAnswerCount(10)}
              >
                10問
              </button>
            </div>
          </div>

          <div className="control-group">
            <label>問題:</label>
            <button onClick={handleNextQuestion} className="question-btn">
              🔄 次の問題
            </button>
          </div>

          <hr />

          <div className="control-group">
            <button
              onClick={() => {
                if (!isRunning) {
                  setMicEnabled(true);
                  setIsRunning(true);
                } else {
                  setIsRunning(false);
                }
              }}
              className="action-btn start"
            >
              {isRunning ? '⏸ 停止' : '▶️ 開始（マイク自動ON）'}
            </button>
          </div>

          <div className="control-group row">
            <button onClick={handleCorrect} className="action-btn correct">⭕ 正解</button>
            <button onClick={handleWrong} className="action-btn wrong">❌ 不正解</button>
          </div>

          <div className="control-group">
            <button onClick={handleReset} className="action-btn reset">🔄 リセット</button>
          </div>

          {voiceSupported && (
            <div className="control-group">
              <button
                onClick={() => setMicEnabled(!micEnabled)}
                className={`action-btn mic ${micEnabled ? 'on' : ''}`}
              >
                {micEnabled ? '🎤 マイクON' : '🔇 マイクOFF'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* ヘッダー：タイマーと進捗 */}
      <div className="tv-header">
        <div className="timer">
          <span className={`timer-value ${remainingTime <= 10 ? 'warning' : ''} ${remainingTime <= 5 ? 'critical' : ''}`}>
            {Math.ceil(remainingTime)}
          </span>
          <span className="timer-label">秒</span>
        </div>
        <div className="title">⚡ ANSWER CHAIN</div>
        <div className="progress">
          <span className="progress-current">{correctCount}</span>
          <span className="progress-sep">/</span>
          <span className="progress-total">{answerCount}</span>
        </div>
      </div>

      {/* 問題表示 */}
      <div className="question-area">
        <p className="question-text">{currentChainQuestion.prompt}</p>
      </div>

      {/* 回答済みリスト */}
      <div className="answers-area">
        {submittedAnswers.map((answer, i) => (
          <div key={i} className="answer-chip">
            <span className="answer-num">{i + 1}</span>
            <span className="answer-text">{answer.text}</span>
          </div>
        ))}
      </div>

      {/* メインエリア：プレイヤーチューブ */}
      <div className="tubes-area">
        {players.map((player, idx) => {
          const isCurrent = idx === currentSlot;
          return (
            <div key={player.playerId} className={`player-tube ${isCurrent ? 'current' : ''}`}>
              {/* チューブ（管） */}
              <div className="tube">
                {/* 隕石（現在のプレイヤーのみ） */}
                {isCurrent && (
                  <div
                    className={`meteor ${meteorPosition > 70 ? 'danger' : ''} ${meteorPosition > 90 ? 'critical' : ''}`}
                    style={{ top: `${Math.min(meteorPosition, 85)}%` }}
                  >
                    <span className="meteor-icon">☄️</span>
                  </div>
                )}
                {/* チューブ内の光 */}
                <div className="tube-glow"></div>
              </div>

              {/* プレイヤーアイコン */}
              <div className={`player-icon ${isCurrent ? 'active' : ''}`}>
                <span className="player-emoji">{player.emoji}</span>
              </div>

              {/* プレイヤー名 */}
              <div className="player-name">
                {player.nickname}
              </div>
            </div>
          );
        })}
      </div>

      {/* 音声入力（常にマウントして自動開始を確実に） */}
      {voiceSupported && (
        <div className="voice-section" style={{ display: micEnabled ? 'block' : 'none' }}>
          {voiceInterim && (
            <div className="voice-interim">認識中: {voiceInterim}</div>
          )}
          <VoiceInput
            onResult={handleVoiceResult}
            onInterim={(text) => setVoiceInterim(text)}
            disabled={!isRunning || !micEnabled}
            autoStart={isRunning && micEnabled}
            keepListening={true}
          />
        </div>
      )}
    </div>
  );
}

export default ChainTVTest;
